/**
 * Pure helpers for the aviso de retenção do vídeo bruto (#526, ADR-INFRA-001
 * D-I2).
 *
 * O prazo de retenção (`VIDEO_BRUTO_RETENTION_DAYS`) não é exposto no
 * contrato HTTP — só o instante já resolvido `bruto_expira_em`
 * (`LevantamentoOut`, espelha a property `Levantamento.bruto_expira_em` em
 * `app/video/models.py`). Este módulo NUNCA recalcula o prazo a partir de
 * `pronto_para_revisao_at`; só deriva EXIBIÇÃO (dias restantes, cor, texto)
 * a partir do datetime que o backend já entrega pronto — mesmo padrão de
 * `fila-helpers.ts` (deriva exibição, nunca estado novo).
 */

export interface RetentionInfo {
  /** Dias até a expiração, arredondado pra cima. Negativo = já expirado. */
  daysRemaining: number;
  expired: boolean;
  label: string;
  colorCls: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Deriva o aviso de retenção a partir de `bruto_expira_em`
 * (`LevantamentoOut`). Retorna `null` quando não há prazo aplicável
 * (`bruto_expira_em` null — o levantamento nunca chegou em
 * 'pronto_para_revisao', #526) ou quando o valor recebido não é um datetime
 * parseável (defensivo — nunca fabrica um prazo a partir de lixo).
 *
 * `now` é injetável (default `new Date()`) pro mesmo motivo de
 * `expire_bruto_videos(..., now=None)` no backend: teste determinístico de
 * borda sem monkeypatch de relógio.
 */
export function computeRetentionInfo(
  brutoExpiraEm: string | null | undefined,
  now: Date = new Date(),
): RetentionInfo | null {
  if (!brutoExpiraEm) return null;

  const expiraMs = Date.parse(brutoExpiraEm);
  if (Number.isNaN(expiraMs)) return null;

  const daysRemaining = Math.ceil((expiraMs - now.getTime()) / MS_PER_DAY);

  if (daysRemaining < 0) {
    return {
      daysRemaining,
      expired: true,
      label: "Bruto expirado — crops preservados",
      colorCls: "bg-tide-text-muted/20 text-tide-text-muted",
    };
  }

  const label =
    daysRemaining === 0
      ? "Bruto expira hoje"
      : `Bruto expira em ${daysRemaining} dia${daysRemaining === 1 ? "" : "s"}`;

  // Amarelo <7 dias (urgência), cinza caso contrário (mesmo racional de
  // "informativo, não crítico" da paleta `filaStatusColorCls`).
  const colorCls =
    daysRemaining < 7
      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
      : "bg-tide-text-muted/20 text-tide-text-muted";

  return { daysRemaining, expired: false, label, colorCls };
}
